import * as mobx from 'mobx';
import * as IoC from 'typescript-ioc';
import { IAuthAggregate } from '../auth.aggregate';
import { IRouter } from '../../../infrastructure/router';
import { RouteParams, Routes } from '../../../routes-table';
import { USER_PERMISSIONS } from '../../../constants/permission.boilerplate';
import { IUserDetailsDTO, IUsersDAO } from '../../../api/users.dao';
import { GET_CURRENT_USER_URL } from '../../../constants/api/users-api';
import TrackingServiceProvider from '../../../tracking-service/tracking-service-provider';
import { IRuntimeConfiguration } from '../../../infrastructure/runtime-configuration';
import { AUTH_ROUTE } from '../../../constants/common.constants';

const REDIRECT_INITIATED_KEY = 'cookie_auth_redirect_initiated';
const REDIRECT_ATTEMPT_KEY = 'cookie_auth_redirect_attempts';
const MAX_REDIRECT_ATTEMPTS = 2;
const LOGOUT_PENDING_KEY = 'cookie_auth_logout_pending';
const FUSION_AUTH_TOKEN_URL = '/fusion-auth/fusion-token';
const REFRESH_LOCK_KEY = 'cookie_auth_refresh_lock';
const REFRESH_LOCK_TTL_MS = 15000;
const REFRESH_LOCK_POLL_MS = 100;
const REFRESH_DONE_KEY = 'cookie_auth_refresh_done';
// Hard cap on a single refresh request. MUST stay below REFRESH_LOCK_TTL_MS so a
// slow/hung refresh aborts (and fails fast) before its cross-tab lock can expire —
// otherwise another tab would treat the still-running refresh as dead, take over,
// and rotate the shared oe_session cookie concurrently.
const REFRESH_TIMEOUT_MS = 10000;
// BE error code (AuthEntryPoint) returned with a 401 when the short-lived session JWT
// timestamp is expired but the underlying session + refresh token are still valid — i.e.
// the session is recoverable via a silent refresh. Distinct from 'auth.session.expired'
// (24h lifetime exhausted) and 'auth.unauthorized' (anonymous / bad token), which are NOT
// recoverable and must fall through to login. Keep in sync with
// Constants.OAuth2ErrorCode.ERROR_CODE_TOKEN_EXPIRED in OE-Connect-Central-BE.
const AUTH_TOKEN_EXPIRED_ERROR_CODE = 'auth.token.expired';

type RefreshLock = {
    ownerId: string;
    expiresAt: number;
};

/**
 * Cookie-based auth aggregate. The backend owns the full OAuth flow via the
 * public-facing fusion-auth/* API Gateway routes (no IAM auth required):
 *   GET  {appOrigin}/fusion-auth/redirect  — builds FusionAuth URL + PKCE, redirects browser
 *   GET  {appOrigin}/fusion-auth/callback  — exchanges code, creates session, sets oe_session HttpOnly cookie
 *   POST {appOrigin}/fusion-auth/refresh   — rotates session, sets new cookie
 *   POST {appOrigin}/fusion-auth/logout    — ends session, clears cookie
 *
 * The frontend never sees or stores access/refresh tokens.
 * All API requests send the cookie automatically (credentials: 'include' in ApiClient).
 *
 * login_hint / SsoIdp flow is deferred — see FusionAuthAggregate for reference.
 */

class CookieAuthAggregate extends IAuthAggregate {
    @mobx.observable
    private authenticated: boolean;

    @mobx.observable
    private permissionAvailable: boolean;

    private userCache: IUserDetailsDTO | null = null;
    private appsyncToken: string | null = null;
    private refreshPromise: Promise<void> | null = null;
    private readonly refreshOwnerId = `${Date.now()}-${Math.random()}`;
    private trackingServiceProvider: TrackingServiceProvider;
    private runtimeConfiguration: IRuntimeConfiguration;

    constructor(
        @IoC.Inject private readonly routingService: IRouter<RouteParams> = IoC.Container.get<IRouter<RouteParams>>(IRouter),
        @IoC.Inject private readonly usersDAO: IUsersDAO = IoC.Container.get(IUsersDAO),
    ) {
        super();
        this.trackingServiceProvider = IoC.Container.get(TrackingServiceProvider);
        this.runtimeConfiguration = IoC.Container.get(IRuntimeConfiguration);
    }

    public initialize = async (): Promise<void> => {
        this.routingService.listen(Routes.LogoutScreen, () => {
            this.authenticate();
        });
        // Check for an incomplete logout from a previous session (network failure on
        // shared device). This runs at the very start of app initialization — before
        // any route or component renders — so the retry fires immediately on page load
        // rather than mid-auth-flow inside getSessionToken().
        if (localStorage.getItem(LOGOUT_PENDING_KEY)) {
            await this.forceLogout();
            // forceLogout() either navigated to logoutUrl (success, page leaves) or
            // re-set the flag (failure). If still failing, redirect to login so the
            // user is not left on a blank page — next load retries forceLogout() again.
            if (localStorage.getItem(LOGOUT_PENDING_KEY)) {
                await this.authenticate();
            }
            return;
        }
        // Routes.AuthCodeFlowCallback is handled by the backend in cookie mode — no listener needed.
        // Routes.SsoIdp / login_hint flow is deferred — to be implemented later.
    };

    public authenticate = async (): Promise<void> => {
        // Guard against redirect loops: if the SPA is loaded again after we already
        // initiated a /fusion-auth/redirect navigation (e.g. because CloudFront served
        // the SPA for that path instead of proxying to the backend), count attempts
        // and stop redirecting after MAX_REDIRECT_ATTEMPTS to avoid cycling indefinitely.
        if (sessionStorage.getItem(REDIRECT_INITIATED_KEY)) {
            sessionStorage.removeItem(REDIRECT_INITIATED_KEY);
            const attempts = parseInt(sessionStorage.getItem(REDIRECT_ATTEMPT_KEY) || '0', 10) + 1;
            sessionStorage.setItem(REDIRECT_ATTEMPT_KEY, String(attempts));
            if (attempts >= MAX_REDIRECT_ATTEMPTS) {
                sessionStorage.removeItem(REDIRECT_ATTEMPT_KEY);
                console.error('[CookieAuth] Redirect loop: /fusion-auth/redirect is not routed to the backend. Auth cannot complete.');
            }
            return;
        }

        const appOrigin = `https://connect.${this.runtimeConfiguration.getAppDomain()}.com`;
        const baseUrl = `${appOrigin}/fusion-auth/redirect`;
        const routeState = this.routingService.router.getState();
        if (routeState) {
            const { path } = routeState;
            if (path.slice(0, AUTH_ROUTE.length) !== AUTH_ROUTE) {
                const redirectUrl = `${appOrigin}${path}`;
                sessionStorage.setItem(REDIRECT_INITIATED_KEY, '1');
                window.location.href = `${baseUrl}?redirectUrl=${encodeURIComponent(redirectUrl)}`;
                return;
            }
        }
        sessionStorage.setItem(REDIRECT_INITIATED_KEY, '1');
        window.location.href = baseUrl;
    };

    public isAuthenticated = (): boolean => this.authenticated;

    public isPermissionAvailable = (): boolean => this.permissionAvailable;

    @mobx.action
    private setAuthenticated(authenticated: boolean) {
        this.authenticated = authenticated;
    }

    @mobx.action
    private setPermissionAvailable(permissionAvailable: boolean) {
        this.permissionAvailable = permissionAvailable;
    }

    /**
     * Fresh session probe bypassing the authenticated cache.
     * Used on 403 to distinguish "cookie deleted → anonymous → 403" from
     * "valid session but no permission → 403".
     * /user/current has no @PreAuthorize → returns 200 for any authenticated user,
     * 403 for anonymous requests.
     */
    public probeSession = async (): Promise<boolean> => {
        try {
            const response = await fetch(
                `${this.runtimeConfiguration.getApiRoot()}${GET_CURRENT_USER_URL}`,
                { credentials: 'include' },
            );
            if (response.ok) {
                this.userCache = await response.json() as IUserDetailsDTO;
                this.setAuthenticated(true);
                return true;
            }
            // 5xx = transient server error — the session may still be valid.
            // Treating 5xx as "no session" causes spurious logout when the BE
            // has a momentary error on /user/current while the oe_session cookie
            // is intact. Only 401/403 definitively mean "anonymous request".
            if (response.status >= 500) {
                return true;
            }
            return false;
        } catch {
            // Network failure — cannot determine session state.
            // Assume valid to avoid logging the user out on connectivity hiccups.
            return true;
        }
    };

    /**
     * In cookie mode the browser automatically includes the oe_session HttpOnly cookie
     * on every request — there is no client-readable token to validate upfront.
     *
     * Validates the session via a raw fetch to /user/current (bypassing APIClientAuthDecorator
     * to avoid circular decorator → getSessionToken calls). Caches the result so subsequent
     * calls within the same session lifecycle return immediately.
     *
     * Returns null when:
     *  - SPA was loaded on a /fusion-auth/* route (backend owns these pages) — no session yet
     *  - The session fetch returns non-200 or throws — user needs to authenticate
     *
     * Returns '' (empty string, not null) on a valid session so:
     *  - AuthGuard's `sessionToken == null` check treats the user as authenticated
     *  - APIClientAuthDecorator's `if (sessionToken)` check is falsy → no Bearer header,
     *    credentials: 'include' is set instead
     */
    public getSessionToken = async (): Promise<string | null> => {
        if (window.location.pathname.startsWith('/fusion-auth/')) {
            // SPA loaded on a BE-owned route — no session exists yet.
            // If the key is also set, this is a redirect loop (CloudFront not proxying).
            return null;
        }
        // On a normal route, a stale key means the callback completed successfully —
        // clear it and proceed to validate the session cookie.
        sessionStorage.removeItem(REDIRECT_INITIATED_KEY);
        if (this.authenticated) {
            return '';
        }
        try {
            let response = await fetch(
                `${this.runtimeConfiguration.getApiRoot()}${GET_CURRENT_USER_URL}`,
                { credentials: 'include' },
            );
            // Cold-start recovery (OEP-36665): with every tab closed, no refresh runs, so the
            // short-lived session JWT expires while the 24h session + refresh token stay valid.
            // The BE signals this recoverable state with 401 + errorCode 'auth.token.expired'.
            // Unlike the APIClientAuthDecorator (which refreshes on 401 during API calls), this
            // cold-start probe previously gave up on any non-200 and bounced the user to login
            // even though a refresh would have succeeded. Attempt a single silent refresh and
            // re-probe before declaring the session dead. Gate strictly on 'auth.token.expired'
            // so genuinely anonymous / 24h-expired sessions still fall through to login and do
            // not get dragged through a needless refresh → forceLogout cycle.
            if (!response.ok
                && !localStorage.getItem(LOGOUT_PENDING_KEY)
                && await this.isRecoverableTokenExpiry(response)) {
                await this.exchangeRefreshTokenForAccessToken();
                // Refresh forced a logout (refresh token itself rejected) — do not re-probe.
                if (!localStorage.getItem(LOGOUT_PENDING_KEY)) {
                    response = await fetch(
                        `${this.runtimeConfiguration.getApiRoot()}${GET_CURRENT_USER_URL}`,
                        { credentials: 'include' },
                    );
                }
            }
            if (response.ok) {
                this.userCache = await response.json() as IUserDetailsDTO;
                this.setAuthenticated(true);
                sessionStorage.removeItem(REDIRECT_ATTEMPT_KEY);
                return '';
            }
            this.setAuthenticated(false);
            return null;
        } catch {
            this.setAuthenticated(false);
            return null;
        }
    };

    /**
     * True only for a 401 whose body carries errorCode 'auth.token.expired' — the one
     * response that means "session is alive, just the short JWT lapsed", so a silent
     * refresh can recover it. A malformed/absent body is treated as non-recoverable so
     * we never trigger a refresh on an ambiguous failure.
     */
    private isRecoverableTokenExpiry = async (response: Response): Promise<boolean> => {
        if (response.status !== 401) return false;
        try {
            const body = await response.clone().json() as { errorCode?: string };
            return body.errorCode === AUTH_TOKEN_EXPIRED_ERROR_CODE;
        } catch {
            return false;
        }
    };

    /**
     * Returns cached user populated by getSessionToken(). If called before
     * getSessionToken() (shouldn't happen in normal flow), falls back to the DAO
     * which goes through the decorator with credentials: include already set.
     */
    public getCurrentUser = async (): Promise<IUserDetailsDTO> => {
        if (!this.userCache) {
            this.userCache = await this.usersDAO.getCurrentUser();
        }
        return this.userCache;
    };

    public getUserPermissions = async (): Promise<void> => {
        this.setPermissionAvailable(false);
        const { isSuperuser, roles } = await this.getCurrentUser();
        localStorage.setItem(USER_PERMISSIONS, JSON.stringify({
            isSuperuser: isSuperuser,
            permissions: Array.from(roles?.reduce((permissionsList, role) => {
                role.permissions?.forEach(permission => permissionsList.add(permission));
                return permissionsList;
            }, new Set()) ?? new Set()),
        }));
        this.setPermissionAvailable(true);
    };

    /**
     * Fetches a FusionAuth access token from the session DB via GET /fusion-auth/fusion-token
     * (authenticated via oe_session cookie). The token is used as authToken for AppSync
     * WebSocket subscriptions, which require an explicit bearer token in connection_init.
     * Cached in memory; cleared on logout. Call again if AppSync returns 401 (token expired).
     */
    public clearAppsyncToken = (): void => {
        this.appsyncToken = null;
    };

    public getAppsyncToken = async (): Promise<string | null> => {
        if (this.appsyncToken) return this.appsyncToken;
        try {
            const response = await fetch(
                `https://connect.${this.runtimeConfiguration.getAppDomain()}.com${FUSION_AUTH_TOKEN_URL}`,
                { credentials: 'include' },
            );
            if (!response.ok) return null;
            const body = await response.json() as { fusionToken?: string };
            this.appsyncToken = body.fusionToken || null;
            return this.appsyncToken;
        } catch {
            return null;
        }
    };

    public logout = async (): Promise<void> => {
        const sessionToken = await this.getSessionToken();
        if (sessionToken != null) {
            await this.forceLogout();
        } else {
            await this.authenticate();
        }
    };

    private forceLogout = async (): Promise<void> => {
        // Set the flag before the network call so it persists even if the request
        // fails (network error, server down). On next app load, getSessionToken()
        // sees the flag and retries logout instead of silently re-authenticating —
        // important when the user logged out on a shared device and connectivity dropped.
        localStorage.setItem(LOGOUT_PENDING_KEY, '1');
        let logoutUrl: string | null = null;
        try {
            const response = await fetch(`https://connect.${this.runtimeConfiguration.getAppDomain()}.com/fusion-auth/logout`, {
                method: 'POST',
                credentials: 'include'
            });
            if (response.ok) {
                const body = await response.json() as { logoutUrl?: string };
                logoutUrl = body.logoutUrl || null;
            }
        } finally {
            this.userCache = null;
            this.appsyncToken = null;
            this.setAuthenticated(false);
            this.setPermissionAvailable(false);
            localStorage.clear();
            this.trackingServiceProvider.get().reset();
            // Navigate to FusionAuth's /oauth2/logout so FA destroys its own SSO
            // session (fusionauth.sso cookie). Without this, FA silently re-authorizes
            // on the next /fusion-auth/redirect call and the user ends up logged back in.
            if (logoutUrl) {
                // Flag cleared implicitly by localStorage.clear() above — navigation proceeds.
                window.location.href = logoutUrl;
            } else {
                // BE unreachable or returned no logoutUrl — persist the flag so the next
                // page load retries forceLogout() via getSessionToken(). Do NOT call
                // authenticate(): the user explicitly logged out and should not be
                // silently redirected back to login.
                localStorage.setItem(LOGOUT_PENDING_KEY, '1');
            }
        }
    };

    /**
     * Token rotation is handled entirely server-side. The backend refreshes the
     * FusionAuth tokens and issues a new oe_session cookie on POST /fusion-auth/refresh.
     * Called by APIClientAuthDecorator on 401 responses (JWT expired inside oe_session).
     * On success the browser stores the new cookie automatically — no reload needed.
     * On failure calls forceLogout() to clear the FA SSO session and redirect to login.
     *
     * Singleton pattern: if a refresh is already in-flight, all concurrent callers
     * receive the same Promise and wait for the single in-flight request to settle.
     * The in-memory Promise covers requests inside one tab; a cross-tab lock covers
     * separate browser tabs, which otherwise have separate JS runtimes and can
     * rotate the shared oe_session cookie at the same time. See
     * _doRefreshOnceAcrossTabs for the Web Locks primary path and localStorage fallback.
     */
    public exchangeRefreshTokenForAccessToken = (): Promise<void> => {
        // Logout is pending — do NOT attempt a silent refresh. The user explicitly
        // terminated the session; refreshing the cookie would silently re-authenticate
        // them, defeating the "amnesia" flag. forceLogout() will be retried on the
        // next getSessionToken() call (next page load or navigation).
        if (localStorage.getItem(LOGOUT_PENDING_KEY)) {
            return Promise.resolve();
        }
        if (this.refreshPromise !== null) return this.refreshPromise;
        this.refreshPromise = this._doRefreshOnceAcrossTabs().finally(() => {
            this.refreshPromise = null;
        });
        return this.refreshPromise;
    };

    /**
     * Refresh exactly once across all open tabs.
     *
     * Primary path: the Web Locks API (navigator.locks) — an atomic, same-origin,
     * cross-tab mutex held for the full duration of the refresh. Only the lock
     * holder rotates the cookie; tabs queued behind it run after release and skip
     * the rotation (the holder's freshly issued cookie is already current). The
     * browser auto-releases the lock if the holder tab is closed mid-refresh, so a
     * queued tab transparently takes over. Being atomic, this removes the
     * read-check-write race and the TTL/polling machinery of the fallback below.
     */
    private _doRefreshOnceAcrossTabs = async (): Promise<void> => {
        const webLocks: LockManager | undefined = navigator.locks;
        if (!webLocks || typeof webLocks.request !== 'function') {
            await this._doRefreshViaStorageLock();
            return;
        }

        const requestedAt = Date.now();
        await webLocks.request(REFRESH_LOCK_KEY, async () => {
            // Logged out elsewhere while we were queued — do NOT re-authenticate.
            if (localStorage.getItem(LOGOUT_PENDING_KEY)) return;
            // Another tab completed a refresh after we started waiting for the lock —
            // its rotated cookie is now current, so a second rotation is both
            // redundant and re-opens the concurrent-rotation race. Skip it.
            if (this.getLastRefreshAt() > requestedAt) return;
            await this._doRefresh();
        });
    };

    /**
     * Best-effort cross-tab coordination for browsers without the Web Locks API.
     * Uses a TTL'd localStorage lock; a slow refresh is bounded by REFRESH_TIMEOUT_MS
     * (< REFRESH_LOCK_TTL_MS) so it can never be mistaken for a dead owner.
     */
    private _doRefreshViaStorageLock = async (): Promise<void> => {
        if (this.tryAcquireRefreshLock()) {
            try {
                await this._doRefresh();
            } finally {
                this.releaseRefreshLock();
            }
            return;
        }

        const lockExpired = await this.waitForRefreshLockRelease();
        if (localStorage.getItem(LOGOUT_PENDING_KEY)) return;
        if (!lockExpired) return;

        if (this.tryAcquireRefreshLock()) {
            try {
                await this._doRefresh();
            } finally {
                this.releaseRefreshLock();
            }
        }
    };

    private getLastRefreshAt = (): number => {
        const value = Number(localStorage.getItem(REFRESH_DONE_KEY));
        return Number.isFinite(value) ? value : 0;
    };

    private tryAcquireRefreshLock = (): boolean => {
        const now = Date.now();
        const currentLock = this.getRefreshLock();
        if (currentLock && currentLock.expiresAt > now && currentLock.ownerId !== this.refreshOwnerId) {
            return false;
        }

        const nextLock: RefreshLock = {
            ownerId: this.refreshOwnerId,
            expiresAt: now + REFRESH_LOCK_TTL_MS,
        };
        localStorage.setItem(REFRESH_LOCK_KEY, JSON.stringify(nextLock));

        return this.getRefreshLock()?.ownerId === this.refreshOwnerId;
    };

    private releaseRefreshLock = (): void => {
        if (this.getRefreshLock()?.ownerId === this.refreshOwnerId) {
            localStorage.removeItem(REFRESH_LOCK_KEY);
        }
    };

    private getRefreshLock = (): RefreshLock | null => {
        const lockJson = localStorage.getItem(REFRESH_LOCK_KEY);
        if (!lockJson) return null;
        try {
            const lock = JSON.parse(lockJson) as Partial<RefreshLock>;
            if (typeof lock.ownerId !== 'string' || typeof lock.expiresAt !== 'number') return null;
            return lock as RefreshLock;
        } catch {
            return null;
        }
    };

    private waitForRefreshLockRelease = (): Promise<boolean> => new Promise(resolve => {
        const resolveIfUnlocked = () => {
            const lock = this.getRefreshLock();
            if (!lock || localStorage.getItem(LOGOUT_PENDING_KEY)) {
                cleanup();
                resolve(false);
            } else if (lock.expiresAt <= Date.now()) {
                cleanup();
                resolve(true);
            }
        };
        const cleanup = () => {
            window.clearInterval(intervalId);
            window.removeEventListener('storage', onStorage);
        };
        const onStorage = (event: StorageEvent) => {
            if (event.key === REFRESH_LOCK_KEY || event.key === LOGOUT_PENDING_KEY) {
                resolveIfUnlocked();
            }
        };
        const intervalId = window.setInterval(resolveIfUnlocked, REFRESH_LOCK_POLL_MS);
        window.addEventListener('storage', onStorage);
        resolveIfUnlocked();
    });

    private _doRefresh = async (): Promise<void> => {
        const controller = new AbortController();
        const timeoutId = window.setTimeout(() => controller.abort(), REFRESH_TIMEOUT_MS);
        try {
            const result = await fetch(`https://connect.${this.runtimeConfiguration.getAppDomain()}.com/fusion-auth/refresh`, {
                method: 'POST',
                credentials: 'include',
                signal: controller.signal,
            });
            if (result.ok) {
                // BE has issued a new oe_session cookie — the browser stores it automatically.
                // No state reset needed: authenticated stays true so getSessionToken() returns ''
                // immediately on the decorator's retry, sending the new cookie transparently.
                // Record completion so tabs queued behind the cross-tab lock skip a redundant rotation.
                localStorage.setItem(REFRESH_DONE_KEY, String(Date.now()));
            } else if (result.status === 401 || result.status === 403) {
                // The refresh token itself was rejected (expired/revoked/invalid) — the
                // session is DEFINITIVELY dead. This is the only response that warrants
                // tearing the session down.
                await this.forceLogout();
            }
            // Any other non-ok status (5xx, 429, 502/503/504, gateway errors) is a TRANSIENT
            // failure of the refresh endpoint — NOT a signal that the user's session is
            // invalid. Mirror the deliberate policy in probeSession()/getSessionToken():
            // keep the session intact and let a later request (or the next refresh) recover,
            // rather than logging the user out of a still-valid session. See OEP-36665.
        } catch {
            // Network error, or AbortError when the refresh exceeds REFRESH_TIMEOUT_MS.
            // Also transient: a slow or briefly-unreachable refresh endpoint does not mean
            // the session is gone, so do NOT forceLogout. The Web Locks / storage lock is
            // released when this scope exits, and the next 401 retries the refresh once
            // connectivity recovers. The timeout still bounds how long the cross-tab lock
            // is held, preventing a hung request from blocking other tabs.
        } finally {
            window.clearTimeout(timeoutId);
        }
    };
}

export { CookieAuthAggregate, LOGOUT_PENDING_KEY };
